import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import platformApiService, { PlatformOperator } from '../services/platformApi';

interface PlatformAuthContextType {
  operator: PlatformOperator | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const PlatformAuthContext = createContext<PlatformAuthContextType | undefined>(undefined);

export const usePlatformAuth = (): PlatformAuthContextType => {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error('usePlatformAuth must be used within a PlatformAuthProvider');
  return ctx;
};

export const PlatformAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [operator, setOperator] = useState<PlatformOperator | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('platformToken');
    const stored = localStorage.getItem('platformOperator');
    if (token && stored) {
      try {
        JSON.parse(stored);
        platformApiService.me().then((response) => {
          if (response.success && response.data) {
            localStorage.setItem('platformOperator', JSON.stringify(response.data));
            setOperator(response.data);
          } else {
            logoutStoredSession();
          }
        });
      } catch {
        localStorage.removeItem('platformToken');
        localStorage.removeItem('platformOperator');
      }
    }
    setIsLoading(false);
  }, []);

  const logoutStoredSession = () => {
    localStorage.removeItem('platformToken');
    localStorage.removeItem('platformOperator');
    setOperator(null);
  };

  const login = async (email: string, password: string) => {
    const response = await platformApiService.login(email, password);
    if (response.success && response.data) {
      localStorage.setItem('platformToken', response.data.token);
      localStorage.setItem('platformOperator', JSON.stringify(response.data.operator));
      setOperator(response.data.operator);
    } else {
      throw new Error(response.error?.message || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('platformToken');
    localStorage.removeItem('platformOperator');
    setOperator(null);
  };

  return (
    <PlatformAuthContext.Provider value={{ operator, isAuthenticated: !!operator, isLoading, login, logout }}>
      {children}
    </PlatformAuthContext.Provider>
  );
};
